import { pdfByType, dynamicAgreementConfig } from '../../../../../helpers/pdfByType';
import {
  DocStep,
  DocStepKind,
  DocStepStatus,
  HiringAgreementApiDoc,
  SignedDocSummary,
  ValidationTokenResponse,
} from '../types';

const PRE_EMPLOYMENT_STEPS: { kind: DocStepKind; title: string; shortLabel: string; description: string }[] = [
  {
    kind: 'PRE_EMPLOYMENT_PERSONAL',
    title: 'Personal Information',
    shortLabel: 'Personal Info',
    description: 'Basic contact and personal details',
  },
  {
    kind: 'PRE_EMPLOYMENT_EMERGENCY',
    title: 'Emergency Contact',
    shortLabel: 'Emergency Contact',
    description: 'Provide emergency contact information',
  },
  {
    kind: 'PRE_EMPLOYMENT_EDUCATION',
    title: 'Education History',
    shortLabel: 'Education',
    description: 'High school, college and credentials',
  },
  {
    kind: 'PRE_EMPLOYMENT_QUESTIONS',
    title: 'Background Questions',
    shortLabel: 'Background',
    description: 'Eligibility and background questions',
  },
  {
    kind: 'PRE_EMPLOYMENT_REFERENCES',
    title: 'Professional References',
    shortLabel: 'References',
    description: 'Up to three professional references',
  },
  {
    kind: 'PRE_EMPLOYMENT_HISTORY',
    title: 'Employment History',
    shortLabel: 'Employment History',
    description: 'Previous jobs and roles',
  },
];

interface BuildStepsArgs {
  tokenData: ValidationTokenResponse;
  signedDocs: SignedDocSummary[];
  draftDocs: SignedDocSummary[];
  application: any;
  applicationDraft: any;
  bankingAccounts: any[];
  localDrafts: Record<string, unknown>;
}

const findByType = (list: SignedDocSummary[], type: string): SignedDocSummary | undefined =>
  list.find((d) => d.type === type);
const hasType = (list: SignedDocSummary[], type: string): boolean => list.some((d) => d.type === type);

const statusFor = (completed: boolean, hasBackendDraft: boolean, hasLocalDraft: boolean): DocStepStatus => {
  if (completed) return 'completed';
  if (hasBackendDraft || hasLocalDraft) return 'partial';
  return 'pending';
};

const preEmploymentMeta = (
  application: any,
  applicationDraft: any,
  hasLocalDraft: boolean,
): { status: DocStepStatus; signatureDate?: string; wasUploaded?: boolean } => {
  if (application?.id) {
    return {
      status: 'completed',
      signatureDate: application?.updatedAt || application?.createdAt,
      wasUploaded: !!application?.uploadedPdf,
    };
  }
  return { status: statusFor(false, !!applicationDraft?.id, hasLocalDraft) };
};

const docMeta = (
  signedDocs: SignedDocSummary[],
  draftDocs: SignedDocSummary[],
  type: string,
  hasLocalDraft: boolean,
): { status: DocStepStatus; signatureDate?: string; wasUploaded?: boolean } => {
  const signed = findByType(signedDocs, type);
  if (signed) {
    return { status: 'completed', signatureDate: signed.signatureDate, wasUploaded: !!signed.uploadedPdf };
  }
  return { status: statusFor(false, hasType(draftDocs, type), hasLocalDraft) };
};

const buildSteps = ({
  tokenData,
  signedDocs,
  draftDocs,
  application,
  applicationDraft,
  bankingAccounts,
  localDrafts,
}: BuildStepsArgs): DocStep[] => {
  const steps: DocStep[] = [];
  const hasLocal = (id: string) => Object.prototype.hasOwnProperty.call(localDrafts, id);

  const hiringAgreements = tokenData.hiringAgreements || [];
  const eligibility = tokenData.employmentEligibilityDocs || [];
  const taxDocs = tokenData.taxDocs || [];
  const otherDocs = tokenData.otherDocs || [];

  const peMeta = preEmploymentMeta(application, applicationDraft, false);
  PRE_EMPLOYMENT_STEPS.forEach((s) => {
    const m = preEmploymentMeta(application, applicationDraft, hasLocal(s.kind));
    steps.push({
      id: s.kind,
      kind: s.kind,
      title: s.title,
      shortLabel: s.shortLabel,
      description: s.description,
      status: m.status,
      signatureDate: m.signatureDate || peMeta.signatureDate,
      wasUploaded: m.wasUploaded ?? peMeta.wasUploaded,
      hasForm: true,
    });
  });

  if (eligibility.includes('I9_ENGLISH')) {
    const m = docMeta(signedDocs, draftDocs, 'I9', hasLocal('I9_ENGLISH'));
    steps.push({
      id: 'I9_ENGLISH',
      kind: 'I9_ENGLISH',
      title: 'I-9 Employment Eligibility',
      shortLabel: 'I-9',
      description: 'Verify your employment authorization',
      status: m.status,
      signatureDate: m.signatureDate,
      wasUploaded: m.wasUploaded,
      hasForm: true,
    });
  }

  if (eligibility.includes('I9_SPANISH')) {
    const m = docMeta(signedDocs, draftDocs, 'I9-Spanish', hasLocal('I9_SPANISH'));
    steps.push({
      id: 'I9_SPANISH',
      kind: 'I9_SPANISH',
      title: 'I-9 Verificación de Empleo',
      shortLabel: 'I-9 (Español)',
      description: 'Verifique su autorización de empleo',
      status: m.status,
      signatureDate: m.signatureDate,
      wasUploaded: m.wasUploaded,
      hasForm: true,
    });
  }

  if (taxDocs.includes('W4')) {
    const m = docMeta(signedDocs, draftDocs, 'W4', hasLocal('W4'));
    steps.push({
      id: 'W4',
      kind: 'W4',
      title: 'W-4 Tax Form',
      shortLabel: 'W-4',
      description: 'Federal tax withholding information',
      status: m.status,
      signatureDate: m.signatureDate,
      wasUploaded: m.wasUploaded,
      hasForm: true,
    });
  }

  if (otherDocs.includes('IT2104')) {
    const m = docMeta(signedDocs, draftDocs, 'IT2104', hasLocal('IT2104'));
    steps.push({
      id: 'IT2104',
      kind: 'IT2104',
      title: 'IT-2104 New York Withholding',
      shortLabel: 'IT-2104',
      description: 'New York State withholding allowances',
      status: m.status,
      signatureDate: m.signatureDate,
      wasUploaded: m.wasUploaded,
      hasForm: true,
    });
  }

  if (otherDocs.includes('IS54')) {
    const m = docMeta(signedDocs, draftDocs, 'IS54', hasLocal('IS54'));
    steps.push({
      id: 'IS54',
      kind: 'IS54',
      title: 'IS-54 Pay Rate Notice',
      shortLabel: 'IS-54',
      description: 'Notice and acknowledgement of pay rate and payday',
      status: m.status,
      signatureDate: m.signatureDate,
      wasUploaded: m.wasUploaded,
      hasForm: true,
    });
  }

  if (otherDocs.includes('NC4')) {
    const m = docMeta(signedDocs, draftDocs, 'NC4', hasLocal('NC4'));
    steps.push({
      id: 'NC4',
      kind: 'NC4',
      title: 'NC-4 North Carolina Withholding',
      shortLabel: 'NC-4',
      description: 'North Carolina State withholding allowances',
      status: m.status,
      signatureDate: m.signatureDate,
      wasUploaded: m.wasUploaded,
      hasForm: true,
    });
  }

  steps.push({
    id: 'BANK_ACCOUNT',
    kind: 'BANK_ACCOUNT',
    title: 'Banking Account',
    shortLabel: 'Banking Account',
    description: 'Add your banking account for direct deposit',
    status: statusFor(bankingAccounts.length > 0, false, hasLocal('BANK_ACCOUNT')),
    hasForm: true,
  });

  const sortedAgreements = [
    ...hiringAgreements.filter((a) => a.category !== 'MANAGEMENT'),
    ...hiringAgreements.filter((a) => a.category === 'MANAGEMENT'),
  ];
  sortedAgreements.forEach((agreement) => buildAgreementStep(agreement, signedDocs, draftDocs, localDrafts, steps));

  steps.push({
    id: 'REVIEW',
    kind: 'REVIEW',
    title: 'Review & Submit',
    shortLabel: 'Review & Submit',
    description: 'Review your information and submit',
    status: 'pending',
    hasForm: true,
  });

  return steps;
};

const CUSTOM_AGREEMENT_KINDS: Record<string, DocStepKind> = {
  EMPLOYEE_CHILDCARE_TUITION: 'EMPLOYEE_CHILDCARE_TUITION',
  LAPTOP_CONTRACT: 'LAPTOP_CONTRACT',
};

const buildAgreementStep = (
  agreement: HiringAgreementApiDoc,
  signedDocs: SignedDocSummary[],
  draftDocs: SignedDocSummary[],
  localDrafts: Record<string, unknown>,
  steps: DocStep[],
) => {
  const staticConfig = pdfByType.find((p) => p.type === agreement.key);
  const customKind = CUSTOM_AGREEMENT_KINDS[agreement.key];
  const id = customKind ? agreement.key : `AGREEMENT_${agreement.id}`;
  const hasLocal = Object.prototype.hasOwnProperty.call(localDrafts, id);
  const m = docMeta(signedDocs, draftDocs, agreement.key, hasLocal);
  steps.push({
    id,
    kind: customKind || 'HIRING_AGREEMENT',
    title: agreement.name,
    shortLabel: agreement.name,
    description: customKind
      ? 'Fill in the required fields and provide your signature'
      : 'Read the document and provide your signature',
    status: m.status,
    signatureDate: m.signatureDate,
    wasUploaded: m.wasUploaded,
    hasForm: !!customKind,
    pdfUrl: staticConfig?.url || agreement.file,
    agreement: {
      ...agreement,
      file: agreement.file || staticConfig?.url,
    },
  });
};

export const isDynamicAgreement = (agreement: HiringAgreementApiDoc): boolean =>
  !pdfByType.find((p) => p.type === agreement.key);

export const getAgreementSignConfig = (agreement: HiringAgreementApiDoc) =>
  pdfByType.find((p) => p.type === agreement.key) || dynamicAgreementConfig;

export default buildSteps;
